/**
 * The measured fit gate — "does this cue fit the design, and if not, how
 * does it split?" (spec 2026-09-17 measured-caption-fit §3.3).
 *
 * ONE rule, three call sites (translate time, Studio design commits, the
 * render safety net): rows are chosen by `selectCaptionRows` — the renderer's
 * own selection — and a cue fits iff its rows respect the cap
 * (`maxLines ?? 2`) AND every row's drawn width stays inside the allowed
 * width (safe area + FIT_PADDING_TOLERANCE of the side padding). A cue that
 * does not fit is split at a balanced clause boundary
 * (`bestSplitIdx`) into sequential cues that tile its time window
 * (`splitCueIntoParts`); parts that still don't fit split again, up to
 * `maxParts`. Text is never dropped, font size is never touched, accent
 * spans survive (words carry their em state through tokenize/serialize).
 *
 * PURE — the measurer factory is injected (font file on the server, canvas
 * in the browser). Idempotent: a fitting list returns `splits: []` and the
 * same array, so running the gate twice is free.
 */
import { bodyMeasurerWithDigits, selectCaptionRows, type MeasureFor } from "./captionRows.ts";
import { bestSplitIdx } from "./captionRules.ts";
import { renderedRowWidth, serializeWords, tokenizeWords, type MeasureFn } from "./captionWrap.ts";
import { splitCueIntoParts } from "./clipEditState.ts";
import { CANVAS_WIDTH, type RenderBrand, type RenderLayout, type SerializableCue } from "../remotion/types.ts";

/**
 * How far a row may run INTO the side padding before the cue is judged not
 * to fit: half of it (per side). The safe area is the wrap's target, but a
 * row 30 px into a 140 px padding is still 110 px from the frame edge —
 * invisible to the eye, while the split that would "fix" it is a visible
 * change of rhythm. Validated on the reported clip (2026-09-17): the two
 * cues that left the frame split; the three that intruded 24–72 px stayed,
 * as the operator judged them; two that fit exactly were left alone.
 * 0 ⇒ the safe area is the hard limit; never more than the canvas.
 */
export const FIT_PADDING_TOLERANCE = 0.5;

const DEFAULT_MAX_PARTS = 4;

/** Each side of a split keeps at least this share of the words (and never
 *  fewer than 2) — a caption of one word is a stub, not a caption. */
const MIN_PART_SHARE = 0.3;

export interface CueSplitOp {
  /** 0-based index against the INPUT cue list. */
  index: number;
  parts: string[];
}

export interface FitOutcome {
  cues: SerializableCue[];
  splits: CueSplitOp[];
}

export interface FitRowsReport {
  rows: string[];
  widths: number[];
  /** The width a row may reach — safe area + the tolerated share of the
   *  side padding; the number the widths are judged against. */
  allowed: number;
  /** The wrap's own target (canvas − padding − box − stroke). */
  usable: number;
  cap: number;
  fits: boolean;
}

/** Row count cap the gate enforces — the product rule (two rows unless the
 *  brand pins otherwise), the same `?? 2` `enforceMaxLines` used. */
function capFor(cap: RenderLayout["caption"]): number {
  return cap.maxLines ?? 2;
}

/** Rows + drawn widths for one cue, measured the way the renderer draws
 *  them: uniform rows at fontSizePx with the last row at the scaled size;
 *  bigPunch top rows at twoLine.topPx (body) and bottom rows at
 *  twoLine.bottomPx (punch font). */
export function fitRowsReport(
  text: string,
  cap: RenderLayout["caption"],
  brand: RenderBrand,
  measFor: MeasureFor,
  tolerance: number = FIT_PADDING_TOLERANCE,
): FitRowsReport {
  const sel = selectCaptionRows(text, cap, brand, measFor);
  const usable = sel.usableWidth;
  const allowed = Math.min(usable + 2 * cap.sidePaddingPx * Math.max(0, tolerance), CANVAS_WIDTH);
  const rowCap = capFor(cap);
  if (sel.bigPunch) {
    const top = bodyMeasurerWithDigits(measFor, brand, cap.twoLine!.topPx);
    const bottom = measFor(brand.punchFont, 400, cap.twoLine!.bottomPx);
    const rows = [...sel.topLines, ...sel.bottomLines];
    const widths = [
      ...sel.topLines.map((r) => renderedRowWidth(r, top)),
      ...sel.bottomLines.map((r) => renderedRowWidth(r, bottom)),
    ];
    return { rows, widths, allowed, usable, cap: rowCap, fits: widths.every((w) => w <= allowed) };
  }
  const base: MeasureFn = bodyMeasurerWithDigits(measFor, brand, cap.fontSizePx);
  const last: MeasureFn = bodyMeasurerWithDigits(measFor, brand, sel.lastLinePx);
  const rows = sel.uniformLines;
  const widths = rows.map((r, i) =>
    renderedRowWidth(r, i === rows.length - 1 && rows.length > 1 ? last : base),
  );
  const fits = rows.length <= rowCap && widths.every((w) => w <= allowed);
  return { rows, widths, allowed, usable, cap: rowCap, fits };
}

export function cueFits(
  text: string,
  cap: RenderLayout["caption"],
  brand: RenderBrand,
  measFor: MeasureFor,
  tolerance: number = FIT_PADDING_TOLERANCE,
): boolean {
  return fitRowsReport(text, cap, brand, measFor, tolerance).fits;
}

/** Split `text` into the fewest parts (≤ maxParts) that each fit; a part
 *  that still can't fit at the limit is kept as is (a single word wider than
 *  the frame is a design problem, not a split problem). Authored newlines are
 *  flattened — the renderer re-wraps each part from the real width anyway.
 *  Em spans survive: words carry their em state through tokenize/serialize. */
function splitToFit(text: string, fits: (t: string) => boolean, maxParts: number): string[] {
  const flat = text.replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim();
  if (maxParts <= 1 || fits(flat)) return [flat];
  const words = tokenizeWords(flat);
  if (words.length < 2) return [flat];
  const minWords = Math.max(2, Math.ceil(words.length * MIN_PART_SHARE));
  const at = bestSplitIdx(words.map((w) => w.plain), minWords);
  const left = serializeWords(words.slice(0, at));
  const right = serializeWords(words.slice(at));
  const leftParts = splitToFit(left, fits, Math.max(1, Math.floor(maxParts / 2)));
  const rightParts = splitToFit(right, fits, Math.max(1, maxParts - leftParts.length));
  return [...leftParts, ...rightParts];
}

export function fitCuesToDesign(
  cues: SerializableCue[],
  cap: RenderLayout["caption"],
  brand: RenderBrand,
  measFor: MeasureFor,
  opts: { tolerance?: number; maxParts?: number } = {},
): FitOutcome {
  // Authored channels keep their authored breaks and the char-budget rule.
  if ((cap.wrapMode ?? "authored") !== "auto") return { cues, splits: [] };
  const tolerance = opts.tolerance ?? FIT_PADDING_TOLERANCE;
  const maxParts = opts.maxParts ?? DEFAULT_MAX_PARTS;
  const fits = (t: string): boolean => cueFits(t, cap, brand, measFor, tolerance);
  const out: SerializableCue[] = [];
  const splits: CueSplitOp[] = [];
  cues.forEach((cue, index) => {
    if (fits(cue.text)) {
      out.push(cue);
      return;
    }
    const parts = splitToFit(cue.text, fits, maxParts);
    if (parts.length < 2) {
      out.push(cue);
      return;
    }
    splits.push({ index, parts });
    out.push(...splitCueIntoParts(cue, parts));
  });
  return splits.length === 0 ? { cues, splits } : { cues: out, splits };
}
